// Hands-free (Features 4, David, 26 Oct; on a Button it was "Run without asking me", Round 7): a chat, Button or job
// whose owner turned it on uses its tools without stopping to ask, reads and writes alike, but still asks before a call
// that sends, deletes or buys (the runner's own list, handsFreeAsks in runner/bin/board-runner.mjs). A Button's own
// second option, "Don't ask before sending either", lets sends go on too; deleting and buying always ask.
//
// Where it is kept, and by whom (only the owner, through the board's session routes): a Button's and a job's in
// props.auto.noAsk (its name from Round 7, true or left out) and a Button's sends in props.auto.noAskSend, which an
// agent is refused as it is the rest of props.auto; a chat's with the chat (chats are the owner's: /api/chats*); the
// default for new ones in Settings, Agents (kv hands-free). The board passes it to the computer only for words the user
// owns: a message they sent in a chat whose switch is on, or a Button's or job's own saved task.

/** The runner that knows Hands-free; an older one is never sent it, and a Button set to run without asking runs as it did. */
export const HANDS_FREE_RUNNER = "0.24.1";

/** Whether a Button's or job's saved settings say it runs hands-free. */
export function withoutAsking(auto: unknown): boolean {
  return !!auto && typeof auto === "object" && (auto as { noAsk?: unknown }).noAsk === true;
}

/** Whether a Button's saved settings let its hands-free runs send without asking too ("Don't ask before sending either"). */
export function sendsToo(auto: unknown): boolean {
  return withoutAsking(auto) && (auto as { noAskSend?: unknown }).noAskSend === true;
}

/**
 * The runner that holds a hands-free job to its rule however the user's own Claude Code or Codex is set (Fixes 8, David,
 * 27 Oct: "a hands-free job should stop before sending"): an older one would let a send their own settings allow go
 * on with nobody watching, so it is sent jobs as they were before Hands-free (the board's own tools alone).
 */
export const HANDS_FREE_JOB_RUNNER = "0.24.2";

/** Whether a runner's version is this one or newer; one it cannot read counts as older. */
function atLeast(version: string | null | undefined, want: string): boolean {
  if (typeof version !== "string" || !/^\d+\.\d+\.\d+/.test(version)) return false;
  const a = version.split(".").map((n) => parseInt(n, 10) || 0);
  const b = want.split(".").map(Number);
  for (let i = 0; i < 3; i++) if (a[i] !== b[i]) return a[i] > b[i];
  return true;
}

/** Whether a runner of this version knows Hands-free: 0.24.1 on (Fixes 8: 0.24.0 let a few tool names that run code go on). */
export function runnerKnowsHandsFree(version: string | null | undefined): boolean {
  return atLeast(version, HANDS_FREE_RUNNER);
}

/** Whether a runner of this version runs a job hands-free: 0.24.2 on, which holds it to the rule (HANDS_FREE_JOB_RUNNER). */
export function runnerHoldsHandsFreeJobs(version: string | null | undefined): boolean {
  return atLeast(version, HANDS_FREE_JOB_RUNNER);
}

/** The words for it, one place: its name, what it does, and what still asks. */
export const HANDS_FREE = {
  name: "Hands-free",
  /** the Button's mark, on hover */
  tip: "Hands-free: runs without asking, but still asks before it sends, deletes or buys",
  tipSends: "Hands-free: runs without asking, sends included. Still asks before it deletes or buys",
  /** the line under a switch that is on */
  on: "Uses your tools without stopping to ask you. It still asks before anything that sends, deletes or buys, like an email.",
  onSends: "Uses your tools without stopping to ask you, and sends without asking too. It still asks before anything that deletes or buys.",
  /** a job, which nobody watches: what would ask is left out, and its answer says so */
  onJob: "Uses your tools without stopping to ask you. Nobody is watching a job, so it never sends, deletes or buys, whatever your own settings allow: it leaves those out and says so.",
  off: "Each run stops to ask you before a tool you haven't allowed yet.",
  /** a job that is not hands-free: it has the board's own tools and your files, and nobody to ask */
  offJob: "Its runs use the board's own tools and your files. Turn this on to let them use your own tools too, like your email.",
  sendsLabel: "Don't ask before sending either",
  sendsOn: "Its runs send emails and posts without asking, like a weekly report. Deleting and buying still ask.",
  sendsOff: "Sending still asks, whatever else runs on its own.",
  /** what an older runner on the linked computer says in place of the switch */
  update: "Update your computer's runner to use Hands-free",
  /** until it updates, a chat asks as it always did and a Button set to run without asking still reads without asking */
  updateHow: (host: string) => `The runner on ${host} updates by itself within a few minutes of opening the board. Until then, it runs as it did before Hands-free.`,
} as const;

/** Why a hands-free call still asked, as the chat says it under the question. */
export function askedBecause(why: string | null | undefined): string | null {
  if (why === "sends") return "Hands-free still asks before anything that sends.";
  if (why === "deletes") return "Hands-free still asks before anything that deletes.";
  if (why === "buys") return "Hands-free still asks before anything that buys.";
  if (why === "unknown") return "Hands-free asks about a tool whose name doesn't say what it does.";
  return null;
}
